import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Flame,
  Globe,
  Mail,
  MessageCircle,
  Phone,
  TrendingDown,
  TrendingUp,
  CreditCard,
  Activity
} from 'lucide-react';

const STATUS_COLOR = {
  Cold: '#38BDF8',
  Warm: '#EAB308',
  Hot: '#F97316',
  Nurture: '#A78BFA',
  'Future Prospect': '#14B8A6',
  Confirmed: '#8B5CF6',
  'Closed Won': '#16A34A',
  'Closed Lost': '#94A3B8'
};

const STATUS_SOFT = {
  Cold: 'bg-sky-50 text-sky-900',
  Warm: 'bg-amber-50 text-amber-950',
  Hot: 'bg-orange-50 text-orange-950',
  Nurture: 'bg-violet-50 text-violet-900',
  'Future Prospect': 'bg-teal-50 text-teal-900',
  Confirmed: 'bg-violet-50 text-violet-950',
  'Closed Won': 'bg-emerald-50 text-emerald-900',
  'Closed Lost': 'bg-slate-100 text-slate-700'
};

const DEFAULT_AXIS = ['Cold', 'Warm', 'Hot', 'Confirmed', 'Closed Won'];
const PX_PER_EVENT = 72;
const CHART_MIN_WIDTH = 420;

function SignalIcon({ signal, className = 'h-3.5 w-3.5' }) {
  if (signal === 'call') return <Phone className={className} />;
  if (signal === 'whatsapp') return <MessageCircle className={className} />;
  if (signal === 'email') return <Mail className={className} />;
  if (signal === 'web') return <Globe className={className} />;
  if (signal === 'payment') return <CreditCard className={className} />;
  return <Activity className={className} />;
}

function monthKey(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  if (!key || key === 'unknown') return 'Unknown';
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/** Group events by calendar month — exact rows preserved, never aggregated away. */
function buildMonthWindows(events) {
  if (!events?.length) return [];
  const map = new Map();
  events.forEach((evt, index) => {
    const key = monthKey(evt.at);
    if (!map.has(key)) {
      map.set(key, { key, label: monthLabel(key), events: [], startIndex: index });
    }
    map.get(key).events.push(evt);
  });
  return Array.from(map.values());
}

function buildSparkPath(points, width, height, padX = 6, padY = 8) {
  if (!points?.length) return { line: '' };
  const levels = points.map((p) => Number(p.level) || 1);
  const minL = Math.min(...levels, 1);
  const maxL = Math.max(...levels, 5);
  const span = Math.max(maxL - minL, 1);
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const n = points.length;

  const coords = points.map((p, i) => {
    const x = n === 1 ? width / 2 : padX + (i / (n - 1)) * innerW;
    const y = padY + innerH - ((Number(p.level) - minL) / span) * innerH;
    return { x, y };
  });

  return {
    line: coords
      .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
      .join(' ')
  };
}

function buildStepGeometry(events, projection, axis, showProjection) {
  const padL = 78;
  const padR = 24;
  const padT = 16;
  const padB = 36;
  const height = 236;
  const nPlot = Math.max(events.length + (showProjection && projection ? 1 : 0), 1);
  const width = Math.max(CHART_MIN_WIDTH, padL + padR + nPlot * PX_PER_EVENT);

  const levelIndex = (pstatus) => {
    const idx = axis.indexOf(pstatus);
    if (idx >= 0) return idx + 1;
    if (pstatus === 'Nurture') return 1.5;
    if (pstatus === 'Future Prospect') return 1.25;
    if (pstatus === 'Closed Lost') return 0.5;
    return 1;
  };

  const minL = 0.5;
  const maxL = axis.length + 0.2;
  const span = maxL - minL;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  const yFor = (level) => padT + innerH - ((level - minL) / span) * innerH;
  const xFor = (i) =>
    nPlot === 1 ? padL + innerW / 2 : padL + (i / (nPlot - 1)) * innerW;

  const coords = events.map((e, i) => ({
    x: xFor(i),
    y: yFor(levelIndex(e.pstatus)),
    event: e
  }));

  let step = '';
  coords.forEach((c, i) => {
    if (i === 0) {
      step = `M ${c.x.toFixed(1)} ${c.y.toFixed(1)}`;
      return;
    }
    const prev = coords[i - 1];
    step += ` L ${c.x.toFixed(1)} ${prev.y.toFixed(1)} L ${c.x.toFixed(1)} ${c.y.toFixed(1)}`;
  });

  let area = '';
  if (coords.length) {
    const last = coords[coords.length - 1];
    const first = coords[0];
    const baseY = padT + innerH;
    area = `${step} L ${last.x.toFixed(1)} ${baseY.toFixed(1)} L ${first.x.toFixed(1)} ${baseY.toFixed(1)} Z`;
  }

  let projectLine = '';
  let projectDot = null;
  if (showProjection && projection && coords.length) {
    const last = coords[coords.length - 1];
    const px = xFor(events.length);
    const py = yFor(levelIndex(projection.pstatus));
    projectLine = `M ${last.x.toFixed(1)} ${last.y.toFixed(1)} L ${px.toFixed(1)} ${last.y.toFixed(1)} L ${px.toFixed(1)} ${py.toFixed(1)}`;
    projectDot = { x: px, y: py, label: projection.label };
  }

  const axisTicks = axis.map((label, i) => ({
    label,
    y: yFor(i + 1),
    color: STATUS_COLOR[label] || '#64748B'
  }));

  return { step, area, coords, projectLine, projectDot, axisTicks, padL, height, width };
}

function statusBadgeClass(pstatus) {
  return STATUS_SOFT[pstatus] || 'bg-slate-100 text-slate-800';
}

function trendTone(trend) {
  if (trend === 'warming' || trend === 'closed') return 'text-emerald-800';
  if (trend === 'cooling') return 'text-rose-700';
  if (trend === 'volatile') return 'text-amber-800';
  if (trend === 'nurture') return 'text-violet-800';
  return 'text-slate-700';
}

function JourneyChart({ events, projection, axis, showProjection }) {
  const geo = useMemo(
    () => buildStepGeometry(events, projection, axis, showProjection),
    [events, projection, axis, showProjection]
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-800">
          Journey
        </p>
        <p className="text-[11px] font-bold tabular-nums text-slate-500">
          {events.length} point{events.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden rounded-xl border border-slate-100 bg-slate-50/40">
        <div style={{ width: geo.width, minWidth: '100%' }}>
          <svg
            viewBox={`0 0 ${geo.width} ${geo.height}`}
            width={geo.width}
            height={210}
            className="block"
            role="img"
            aria-label="Lead temperature journey chart">
            <defs>
              <linearGradient id="journeyFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FDBA74" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#FED7AA" stopOpacity="0.05" />
              </linearGradient>
            </defs>

            {geo.axisTicks.map((tick) => (
              <g key={tick.label}>
                <line
                  x1={geo.padL - 8}
                  x2={geo.width - 16}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="#E2E8F0"
                  strokeWidth="1"
                />
                <circle cx={14} cy={tick.y} r={4.5} fill={tick.color} />
                <text
                  x={24}
                  y={tick.y + 4}
                  className="fill-slate-700"
                  style={{ fontSize: 11, fontWeight: 800 }}>
                  {tick.label}
                </text>
              </g>
            ))}

            {geo.area ? <path d={geo.area} fill="url(#journeyFill)" /> : null}
            {geo.step ? (
              <path
                d={geo.step}
                fill="none"
                stroke="#0F172A"
                strokeWidth="2.25"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ) : null}

            {geo.projectLine ? (
              <path
                d={geo.projectLine}
                fill="none"
                stroke="#16A34A"
                strokeWidth="2"
                strokeDasharray="5 4"
                strokeLinecap="round"
              />
            ) : null}
            {geo.projectDot ? (
              <circle
                cx={geo.projectDot.x}
                cy={geo.projectDot.y}
                r={6}
                fill="#16A34A"
                stroke="#fff"
                strokeWidth="2"
              />
            ) : null}

            {geo.coords.map((c) => (
              <g key={c.event.id}>
                {c.event.cooled ? (
                  <>
                    <line
                      x1={c.x}
                      y1={c.y - 16}
                      x2={c.x}
                      y2={c.y - 8}
                      stroke="#EF4444"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                    />
                    <path
                      d={`M ${c.x - 3.5} ${c.y - 11} L ${c.x} ${c.y - 6} L ${c.x + 3.5} ${c.y - 11}`}
                      fill="none"
                      stroke="#EF4444"
                      strokeWidth="2.25"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </>
                ) : null}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={5.5}
                  fill="#fff"
                  stroke={STATUS_COLOR[c.event.pstatus] || '#0F172A'}
                  strokeWidth="2.75"
                />
                <title>{`${c.event.pstatus} · ${c.event.label} · ${c.event.description}`}</title>
                <text
                  x={c.x}
                  y={geo.height - 10}
                  textAnchor="middle"
                  className="fill-slate-700"
                  style={{ fontSize: 10, fontWeight: 800 }}>
                  {c.event.label}
                </text>
              </g>
            ))}
          </svg>

          <div
            className="relative -mt-1 mb-2 h-4"
            style={{ marginLeft: geo.padL, marginRight: 24, width: geo.width - geo.padL - 24 }}>
            {geo.coords.map((c) => (
              <span
                key={`sig-${c.event.id}`}
                className="absolute top-0 -translate-x-1/2 text-slate-500"
                style={{ left: c.x - geo.padL }}
                title={c.event.signal}>
                <SignalIcon signal={c.event.signal} className="h-3.5 w-3.5" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2.5 text-[10px] font-bold text-slate-600">
          <span className="font-extrabold text-slate-800">Signals</span>
          {[
            ['call', 'Call'],
            ['whatsapp', 'Whatsapp'],
            ['email', 'Email'],
            ['web', 'Web'],
            ['payment', 'Payment']
          ].map(([sig, label]) => (
            <span key={sig} className="inline-flex items-center gap-1">
              <SignalIcon signal={sig} className="h-3 w-3" />
              {label}
            </span>
          ))}
        </div>
        {showProjection && projection?.summary ? (
          <p className="text-[10px] font-extrabold text-emerald-800">--- {projection.summary}</p>
        ) : null}
      </div>
    </div>
  );
}

function FunnelList({ events }) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-800">
          Funnel
        </p>
        <p className="text-[11px] font-bold tabular-nums text-slate-500">
          {events.length} event{events.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden rounded-xl border border-slate-100 bg-slate-50/40 px-2.5 py-1 max-h-[280px]">
        {events.length === 0 ? (
          <p className="py-6 text-center text-xs font-bold text-slate-500">No events in this month</p>
        ) : (
          <ul className="divide-y divide-slate-200/80">
            {events.map((evt) => (
              <li key={evt.id} className="flex items-start gap-2.5 py-2.5 first:pt-1.5 last:pb-1.5">
                <div className="w-[3.25rem] shrink-0 pt-0.5 text-[11px] font-extrabold tabular-nums text-slate-600">
                  {evt.label}
                </div>
                <div
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    STATUS_SOFT[evt.pstatus] || 'bg-slate-100 text-slate-700'
                  }`}>
                  <SignalIcon signal={evt.signal} className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-extrabold text-slate-900">{evt.pstatus}</span>
                    {evt.cooled ? (
                      <span className="inline-flex items-center gap-0.5 text-[11px] font-extrabold text-rose-700">
                        <TrendingDown className="h-3 w-3" />
                        cooled
                      </span>
                    ) : null}
                  </div>
                  <p
                    className="mt-0.5 max-h-10 overflow-y-auto text-xs font-semibold leading-snug text-slate-700 pr-1"
                    title={evt.description}>
                    {evt.description}
                  </p>
                </div>
                <div className="shrink-0 pt-0.5 text-xs font-extrabold tabular-nums text-slate-500">
                  {evt.durationDays != null ? `${evt.durationDays}d` : '—'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MonthPager({ months, monthIndex, onChange }) {
  if (!months?.length) return null;
  const current = months[monthIndex];
  const multi = months.length > 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={!multi || monthIndex <= 0}
          onClick={() => onChange(monthIndex - 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-slate-100"
          aria-label="Previous month">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="min-w-[7.5rem] text-center">
          <p className="text-sm font-extrabold text-slate-900">{current?.label}</p>
          <p className="text-[10px] font-bold text-slate-500">
            {current?.events?.length || 0} exact event
            {(current?.events?.length || 0) === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          disabled={!multi || monthIndex >= months.length - 1}
          onClick={() => onChange(monthIndex + 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-slate-100"
          aria-label="Next month">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="flex max-w-full items-center gap-1 overflow-x-auto pb-0.5">
        {months.map((m, i) => (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange(i)}
            className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-extrabold transition ${
              i === monthIndex
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:text-slate-900'
            }`}>
            {m.label}
            <span className="ml-1 opacity-70">{m.events.length}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Collapsible Temperature Movement — Journey + Funnel side-by-side,
 * month windows keep exact event rows when history spans many months.
 */
export default function LeadTemperatureMovementCard({
  movement,
  isLoading = false,
  leadName = null
}) {
  const [expanded, setExpanded] = useState(false);
  const [monthIndex, setMonthIndex] = useState(0);

  const allEvents = useMemo(() => {
    if (movement?.events?.length) return movement.events;
    return movement?.points || [];
  }, [movement]);

  const months = useMemo(() => buildMonthWindows(allEvents), [allEvents]);
  const monthsSignature = useMemo(
    () => months.map((m) => `${m.key}:${m.events.length}`).join('|'),
    [months]
  );

  useEffect(() => {
    if (!months.length) {
      setMonthIndex(0);
      return;
    }
    setMonthIndex(months.length - 1);
  }, [monthsSignature, months.length]);

  const safeMonthIndex = Math.min(Math.max(monthIndex, 0), Math.max(months.length - 1, 0));
  const activeMonth = months[safeMonthIndex] || null;
  const monthEvents = activeMonth?.events || [];
  const isLatestMonth = safeMonthIndex === months.length - 1;

  const axis = movement?.axis?.length ? movement.axis : DEFAULT_AXIS;
  const displayName = movement?.leadName || leadName || 'Lead';
  const eventCount = movement?.eventCount ?? allEvents.length;
  const trend = movement?.trend || 'stable';
  const isPositiveTrend = trend === 'warming' || trend === 'closed';

  const spark = useMemo(
    () => buildSparkPath(movement?.points || allEvents, 120, 36),
    [movement?.points, allEvents]
  );

  if (isLoading) {
    return (
      <div className="mb-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-40 rounded bg-slate-100" />
            <div className="h-3 w-48 rounded bg-slate-100" />
          </div>
          <div className="h-9 w-28 rounded bg-slate-100" />
        </div>
      </div>
    );
  }

  if (!movement?.points?.length && !movement?.events?.length) return null;

  return (
    <div className="mb-3 space-y-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition hover:border-slate-300 hover:bg-slate-50/40">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
          <Flame className="h-4 w-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-extrabold text-slate-900">Temperature Movement</span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-extrabold ${statusBadgeClass(
                movement.current
              )}`}>
              {movement.current}
            </span>
          </div>
          <p
            className={`mt-0.5 flex flex-wrap items-center gap-1 text-xs font-bold ${trendTone(
              trend
            )}`}>
            {isPositiveTrend ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : trend === 'cooling' ? (
              <TrendingDown className="h-3.5 w-3.5" />
            ) : (
              <Flame className="h-3.5 w-3.5" />
            )}
            <span>
              {movement.trendLabel || 'Holding steady'} · {eventCount} event
              {eventCount === 1 ? '' : 's'} tracked
            </span>
          </p>
        </div>

        <div className="hidden sm:block shrink-0 w-[120px]">
          <svg viewBox="0 0 120 36" className="h-9 w-full" aria-hidden="true">
            {spark.line ? (
              <path
                d={spark.line}
                fill="none"
                stroke="#16A34A"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null}
          </svg>
        </div>

        <span className="shrink-0 text-slate-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {expanded ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm sm:p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-slate-500">
                Deal
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <h4 className="max-w-full truncate text-base font-extrabold text-slate-900 sm:max-w-md">
                  {displayName}
                </h4>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-extrabold ${statusBadgeClass(
                    movement.current
                  )}`}>
                  {movement.current}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-extrabold ring-1 ${
                    isPositiveTrend
                      ? 'bg-emerald-50 text-emerald-900 ring-emerald-100'
                      : trend === 'cooling'
                        ? 'bg-rose-50 text-rose-800 ring-rose-100'
                        : 'bg-slate-50 text-slate-800 ring-slate-200'
                  }`}>
                  {isPositiveTrend ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : trend === 'cooling' ? (
                    <TrendingDown className="h-3 w-3" />
                  ) : null}
                  {movement.trendLabel}
                </span>
              </div>
              {movement.analysis ? (
                <div className="mt-1.5 max-h-12 overflow-y-auto pr-1">
                  <p className="text-xs font-bold leading-relaxed text-slate-700">
                    {movement.analysis}
                  </p>
                </div>
              ) : null}
            </div>

            {movement.lastMovementLabel ? (
              <p className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500">
                <Clock className="h-3.5 w-3.5" />
                {movement.lastMovementLabel}
              </p>
            ) : null}
          </div>

          <div className="mb-3">
            <MonthPager months={months} monthIndex={safeMonthIndex} onChange={setMonthIndex} />
          </div>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 items-stretch">
            <div className="min-w-0 min-h-[300px]">
              <JourneyChart
                events={monthEvents}
                projection={movement.projection}
                axis={axis}
                showProjection={isLatestMonth}
              />
            </div>
            <div className="min-w-0 min-h-[300px]">
              <FunnelList events={monthEvents} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
